import type { DeviceNode } from '../types';
import { getOperatorLabel, formatValue, toString } from './parse-helpers';

/**
 * Parse device conditions.
 * Handles: { event: { type: 'device_change', name, filters: { and: [to, from] } } }
 *          { type: 2, attribute_filter: { ... } }
 *          { type: 'device_attribute' | 'device_change', field, operator, value }
 */
export function parseDevice(raw: Record<string, unknown>): DeviceNode | null {
  // Ember format: { event: { type: 'device_change', name, filters } }
  const event = raw.event as Record<string, unknown> | undefined;
  if (event?.type === 'device_change') {
    const field = toString(event.name) ?? 'device';
    const filters = event.filters as Record<string, unknown> | undefined;
    const andFilters = filters?.and as Record<string, unknown>[] | undefined;
    const toFilter = andFilters?.[0];
    const operator = toString(toFilter?.operator);
    const inverse = toFilter?.inverse === true;
    return {
      type: 'device',
      field,
      operator: operator != null ? getOperatorLabel(operator, inverse) : undefined,
      value: toFilter?.value != null ? formatValue(toFilter.value, operator) : undefined,
      value2: toFilter?.value2 != null ? formatValue(toFilter.value2, operator) : undefined,
      inverse,
    };
  }

  // Backend format: { type: 2, attribute_filter: { ... } }
  if (
    (raw.type === 2 || raw.type === '2') &&
    typeof raw.attribute_filter === 'object' &&
    raw.attribute_filter != null
  ) {
    const af = raw.attribute_filter as Record<string, unknown>;
    const field = toString(af.attribute ?? af.field) ?? 'device';
    const operator = toString(af.operator);
    const inverse = af.inverse === true;
    return {
      type: 'device',
      field,
      operator: operator != null ? getOperatorLabel(operator, inverse) : undefined,
      value: af.value != null ? formatValue(af.value, operator) : undefined,
      value2: af.value2 != null ? formatValue(af.value2, operator) : undefined,
      inverse,
    };
  }

  // Direct format
  if (raw.type === 'device_attribute' || raw.type === 'device_change') {
    const field = toString(raw.field ?? raw.attribute ?? raw.name) ?? 'device';
    const operator = toString(raw.operator);
    const inverse = raw.inverse === true;
    return {
      type: 'device',
      field,
      operator: operator != null ? getOperatorLabel(operator, inverse) : undefined,
      value: raw.value != null ? formatValue(raw.value, operator) : undefined,
      value2: raw.value2 != null ? formatValue(raw.value2, operator) : undefined,
      inverse,
    };
  }

  return null;
}
